import { type TranslationKey, useTranslations } from '@heidi/runtime/i18n';
import { SubscriptionStatus } from '@heidi/schema/ml-scribe';
import { useMemo } from 'react';

/** Key suffix for plan-specific upgrade translation keys (e.g. upgrade.titleTrialEndsTodayClinician). */
export enum PlanKeySuffix {
  Clinician = 'Clinician',
  EvidencePlus = 'EvidencePlus',
  Practice = 'Practice',
}

/**
 * Returns the display name of the user's current plan for trial upgrade modals.
 * For new sign-ups (reverse trial) uses "Free"; otherwise maps previous status to plan name.
 */
export function getCurrentPlanNameForTrial(
  isReverseTrial: boolean,
  previousStatusBeforeTrial: string | null | undefined
): 'Free' | 'Pro' | 'Practice' {
  if (isReverseTrial) return 'Free';
  switch (previousStatusBeforeTrial) {
    case SubscriptionStatus.PRO:
    case SubscriptionStatus.PRO_TRIAL:
      return 'Pro';
    case SubscriptionStatus.PRO_PLUS:
    case SubscriptionStatus.PRO_PLUS_TRIAL:
      return 'Practice';
    default:
      return 'Free';
  }
}

export interface UseUpgradeModalTrialTextParams {
  /** Number of trial days remaining (or left). */
  trialDaysLeft: number;
  /** When true, title shows the expired/level-up message instead of days-based title. */
  showExpiredTitle: boolean;
  /** Translation key for title when trial is expired (e.g. 'practiceTrial.upgradeModal.titleLevelUpCare'). */
  titleWhenExpiredKey: string;
  /** When set, headline uses plan-specific trial title keys. When trialDaysLeft > 3, uses benefit-led title. */
  plan?: PlanKeySuffix;
  /** When set and trialDaysLeft > 3, featuresTitle uses "Everything in [currentPlanName], plus:" when currentPlanName is set. */
  everythingInPlanPlusKey?: TranslationKey;
  /** Current plan name for the "Everything in [plan], plus:" label (e.g. "Free", "Pro", "Practice"). When provided and trialDaysLeft > 3, used with upgrade.everythingInPlanPlus. */
  currentPlanName?: string;
}

/**
 * Shared title and lose-access copy for upgrade modals (Practice, Clinician, Evidence Pro).
 * Centralizes days-based and expired-state messaging with plan-specific titles and "soon"
 * emphasis for 1–3 days.
 */
export function useUpgradeModalTrialText({
  trialDaysLeft,
  showExpiredTitle,
  titleWhenExpiredKey,
  plan,
  everythingInPlanPlusKey,
  currentPlanName,
}: UseUpgradeModalTrialTextParams) {
  const { t } = useTranslations();

  const titleText = useMemo(() => {
    if (showExpiredTitle) {
      return t(titleWhenExpiredKey as TranslationKey);
    }
    const suffix = plan ?? null;
    if (trialDaysLeft > 3 && suffix) {
      return t(`upgrade.titleFreeTrial${suffix}` as TranslationKey);
    }
    if (trialDaysLeft <= 3 && suffix) {
      if (trialDaysLeft <= 0) {
        return t(`upgrade.titleLastDay${suffix}` as TranslationKey);
      }
      return t(`upgrade.titleDaysLeftToKeep${suffix}` as TranslationKey, {
        days: trialDaysLeft,
        count: trialDaysLeft,
      });
    }
    if (trialDaysLeft <= 0) {
      return t('upgrade.titleTrialEndsToday');
    }
    if (trialDaysLeft === 1) {
      return t('upgrade.titleTrialEndsInOneDay');
    }
    if (trialDaysLeft <= 3) {
      return t('upgrade.trialEndsInDaysSoon', { days: trialDaysLeft });
    }
    return t('upgrade.trialEndsInDays', { days: trialDaysLeft });
  }, [trialDaysLeft, showExpiredTitle, titleWhenExpiredKey, t, plan]);

  const loseAccessText = useMemo(() => {
    if (showExpiredTitle) {
      return undefined;
    }
    return trialDaysLeft <= 0
      ? t('upgrade.loseAccessToday')
      : t('upgrade.loseAccessDays' as TranslationKey, {
          count: trialDaysLeft,
          days: trialDaysLeft,
        });
  }, [trialDaysLeft, showExpiredTitle, t]);

  const featuresTitle = useMemo(() => {
    if (showExpiredTitle) {
      return undefined;
    }
    if (trialDaysLeft > 3) {
      if (currentPlanName !== undefined && currentPlanName !== null) {
        return t('upgrade.everythingInPlanPlus' as TranslationKey, {
          planName: currentPlanName,
        });
      }
      if (everythingInPlanPlusKey) {
        return t(everythingInPlanPlusKey);
      }
    }
    return loseAccessText ?? undefined;
  }, [
    showExpiredTitle,
    trialDaysLeft,
    everythingInPlanPlusKey,
    currentPlanName,
    loseAccessText,
    t,
  ]);

  const isShortTrial = !showExpiredTitle && trialDaysLeft <= 3;

  return { titleText, loseAccessText, featuresTitle, isShortTrial };
}
